<template>
    <bottom-sheet v-model="shows" title="개인회원 면접요청" fullscreen persistent width="1080" maxWidth="95%" needsLogin>
        <template #activator="{ attrs, on }">
            <slot name="activator" v-bind="{ attrs, on, loading }" />
        </template>
        <v-layout column justify-center class="bottom-sheet__contents h-100">
            <template v-if="loading">
                <v-overlay absolute color="white">
                    <v-progress-circular indeterminate size="100" width="5" color="primary" />
                </v-overlay>
            </template>
            <template v-else>
                <div v-if="step == STEPS.COMMON.value" class="d-flex flex-column fill-height">
                    <div class="text-center mb-20 mb-lg-40">
                        <h3 class="tit mb-8 mb-lg-16">면접시간 확인</h3>
                        <p class="page-text--lg grey-6--text">
                            <span class="primary--text">
                                {{ employee.info.name }}
                            </span>
                            개인회원에게
                            <template v-if="employer?.interview?.isOnline">
                                <span class="lightblue--text"> 온라인 </span>
                            </template>
                            <template v-else>
                                <span class="u-green-5a--text"> 오프라인 </span>
                            </template>
                            면접을 요청합니다.
                        </p>
                    </div>
                    <v-sheet class="scroll-primary pr-10" style="flex: 0 1 auto; overflow-y: auto; overflow-x: hidden">
                        <common-loca v-bind="{ employer }" class="mb-20 mb-lg-40" />
                        <expo-matching-table
                            v-model="form"
                            v-bind="{
                                program,
                                employer,
                                matchings,
                            }"
                            mode="read"
                            class="mb-20 mb-lg-40"
                            @input="emit"
                        />
                        <employee-info />
                    </v-sheet>
                    <div class="v-btn-group">
                        <v-btn outlined color="grey-d" class="min-w-140px min-w-lg-160px" @click="exit">
                            <span class="grey-6--text"> 취소하기 </span>
                        </v-btn>
                        <v-btn color="primary" class="min-w-140px min-w-lg-160px" @click="save">
                            <span> 요청하기 </span>
                        </v-btn>
                    </div>
                </div>
                <div v-if="step == STEPS.FINISH.value" class="d-flex flex-column">
                    <v-sheet class="d-flex flex-column align-center">
                        <i class="icon icon-check mb-20 mb-lg-40"></i>
                        <v-sheet class="text-center mb-20 mb-lg-40">
                            <h3 class="tit mb-8 mb-lg-16">면접요청 완료</h3>
                            <p class="page-text--lg grey-6--text">
                                <span class="primary--text">
                                    {{ employee.info.name }}
                                </span>
                                개인회원에게
                                <template v-if="employer?.interview?.isOnline">
                                    <span class="lightblue--text"> 온라인 </span>
                                </template>
                                <template v-else>
                                    <span class="u-green-5a--text"> 오프라인 </span>
                                </template>
                                면접에 대한 요청이 완료되었습니다.
                            </p>
                        </v-sheet>
                        <div class="v-btn-group mt-0">
                            <v-btn color="primary" class="min-w-140px min-w-lg-160px" @click="exit"><span>확인</span></v-btn>
                        </div>
                    </v-sheet>
                </div>
            </template>
        </v-layout>
    </bottom-sheet>
</template>

<script>
import api from "@/api";
import { EXPO_MATCHING_DIRECTIONS } from "@/assets/variables";
import { initExpoEmployee, initExpoEmployer, initExpoMatching, initExpoProgram } from "@/assets/variables/inits";

import BottomSheet from "@/components/dumb/bottom-sheet.vue";
import ExpoMatchingTable from "@/components/client/expo/matching/expo-matching-table.vue";
import EmployeeInfo from "./employee/employee-info.vue";
import CommonLoca from "./common/common-loca.vue";

const STEPS = {
    COMMON: { value: "COMMON", text: "기본정보" },
    FINISH: { value: "FINISH", text: "신청완료" },
};

const steps = Object.values(STEPS).map((item, index) => ({ ...item, step: index + 1 }));

export default {
    components: {
        BottomSheet,
        ExpoMatchingTable,
        EmployeeInfo,
        CommonLoca,
    },
    props: {
        value: { type: Object, default: initExpoMatching },
        program: { type: Object, default: initExpoProgram },
        employee: { type: Object, default: initExpoEmployee },
        employer: { type: Object, default: initExpoEmployer },
    },
    data: () => ({
        form: initExpoMatching(),
        matchings: [],

        shows: false,
        loading: false,

        step: steps[0].value,
        steps,
        STEPS,
    }),
    computed: {
        isCreate() {
            return !this.value?._id;
        },
        _program() {
            return this.program._id;
        },
        _employee() {
            return this.employee._id;
        },
        _employer() {
            return this.employer._id;
        },
    },
    methods: {
        async init() {
            if (this.loading) return;
            else this.loading = true;

            this.step = steps[0].value;

            try {
                const { _program } = this;

                if (this.isCreate) {
                    this.form = initExpoMatching({
                        _program,

                        _employee: this.employee?._id,
                        _employeeUser: this.employee?._user,

                        _employer: this.employer?._id,
                        _employerUser: this.employer?._user,

                        direction: EXPO_MATCHING_DIRECTIONS.TO_EMPLOYEE.value,
                    });
                } else {
                    let { matching } = await api.v1.company.expo.matchings.get({
                        _id: this.value._id,
                    });
                    this.form = initExpoMatching(matching);
                }

                const { _employee, _employer } = this;
                const isGranted = JSON.stringify({ $in: [null, true] });

                let { matchings: matchings_employee } = await api.v1.expo.programs.matchings.gets({
                    _program,
                    params: {
                        _employee,
                        isGranted,
                    },
                });

                let { matchings: matchings_employer } = await api.v1.expo.programs.matchings.gets({
                    _program,
                    params: {
                        _employer,
                        isGranted,
                    },
                });

                this.matchings = [...matchings_employer, ...matchings_employee];
            } finally {
                this.loading = false;
            }
        },
        async exit() {
            this.shows = false;

            if (this.form._id) {
                this.$emit("input", this.form);
            }
        },
        async next() {
            if (!this.validate()) return;

            let index = this.steps.findIndex(({ value }) => value == this.step);
            this.step = this.steps[index + 1]?.value;
        },
        async prev() {
            let index = this.steps.findIndex(({ value }) => value == this.step);
            this.step = this.steps[index - 1]?.value;
        },
        async save() {
            if (!this.validate()) return;

            if (this.loading) return;
            else this.loading = true;
            try {
                let { ...form } = this.form;

                let { post, put } = api.v1.company.expo.matchings;
                let { matching } = await (this.isCreate ? post : put)(form);
                this.form = initExpoMatching(matching);
                this.next();
            } finally {
                if (this.loading) this.loading = false;
            }
        },

        emit() {
            this.form = initExpoMatching(this.form);
        },

        validate() {
            try {
                switch (this.step) {
                    case STEPS.COMMON.value: {
                        break;
                    }
                }

                return true;
            } catch (error) {
                alert(error.message);
                return false;
            }
        },
    },
    watch: {
        _employee() {
            this.init();
        },
        _employer() {
            this.init();
        },
        _program() {
            this.init();
        },
        _user() {
            this.init();
        },
        shows(shows) {
            if (shows) this.$nextTick(this.init);
            else if (this.form._id) {
                this.$emit("input", this.form);
            }
        },
    },
    mounted() {
        this.init();
    },
};
</script>

<style lang="scss" scoped>
::v-deep .v-stepper__wrapper {
    display: flex;
    flex-direction: column;
}
</style>
